import React, { useCallback } from "react";
import cx from "classnames";
import NiceModal from "@ebay/nice-modal-react";
import { Product3dViewModal } from "components/_Modals/Product3dViewModal/Product3dViewModal";
import { SingleProductDownloadModal } from "components/_Modals/ProjectDownloadModal/SingleProductDownloadModal";
import {
  useAddedProductStore,
  useCopiedItemStore,
  useProjectDataStore,
} from "stores/projectStore";
import { useDeleteConfirmationCheck } from "components/ProjectTree/hooks";
import { ProductDetails } from "components/ProjectTree/components/Configuration/ProductDetails";
import { ProductImage } from "components/ProjectTree/components/Configuration/ProductImage";
import { ConfigurationControls } from "./ConfigurationControls";
import s from "../Styles.module.scss";

export const Configuration = ({
  node,
  options,
  translations,
  navigateToProduct,
  nodeActions,
  handleToggleEditMode,
  setIsEditMode,
  isEditMode,
  lang,
  navigateToCustomProduct,
}) => {
  const units = useProjectDataStore((state) => state.units);
  const copiedItem = useCopiedItemStore((state) => state.copiedItem);
  const { data } = node;
  const isPasteMode = !!copiedItem;
  const deleteConfirmationCheck = useDeleteConfirmationCheck();

  const addedProducts = useAddedProductStore((state) => state.addedProducts);
  const canDeleteInAddToProjectModal =
    !!options?.productAddingModal && addedProducts?.includes(data?.id);

  const handleDelete = useCallback(() => {
    deleteConfirmationCheck(node, () => nodeActions.deleteNode(node));
  }, [deleteConfirmationCheck, node, nodeActions]);

  const handleToggle3dView = useCallback(() => {
    NiceModal.show(Product3dViewModal, {
      requestParams: {
        seriesId: data?.orderCode?.seriesId,
        state: {
          orderCodeState: data?.orderCode?.state,
          currentStrategyId: data?.orderCode?.currentStrategyId,
        },
        resultUnitsSystem: units,
        languageIso2L: lang,
      },
    });
  }, [lang, data, units]);

  const openDownloadModal = useCallback(() => {
    NiceModal.show(SingleProductDownloadModal, {
      modalSubtitle: data?.productName,
      modalType: "Product",
      orderCode: data?.orderCode,
      presentation: data?.presentation,
      nodeId: data?.id,
      lang,
    });
  }, [data, lang]);

  return (
    <div className="d-flex h-100 w-100 flex-row align-items-center justify-content-between overflow-hidden">
      <div
        className="d-flex flex-row align-items-center gap-2 h-100 overflow-hidden w-100"
        style={{ minWidth: 0 }}
      >
        <ProductImage
          pimId={data.pimId}
          productImagesDisabled={options.productImagesDisabled}
          productName={data.productName}
          width={64}
          height={64}
        />
        <ProductDetails
          options={options}
          isWizard={data.isWizard}
          productName={data.productName}
          presentation={data.presentation}
          navigateToProduct={navigateToProduct}
          isPasteMode={isPasteMode}
          isEditMode={isEditMode}
          setIsEditMode={setIsEditMode}
          handleUpdateProductName={(productName) =>
            nodeActions.updateNode(node, {
              productName,
              presentation: data?.presentation,
            })
          }
          handleToggleEditMode={handleToggleEditMode}
          navigateToCustomProduct={navigateToCustomProduct}
          data={data}
        />
      </div>
      <div
        className={cx({
          [s.configControlsContainer]: options.isFullpage,
        })}
      >
        <ConfigurationControls
          configId={node?.data?.id}
          isEditMode={isEditMode}
          data={data}
          options={options}
          translations={translations}
          handleToggleEditMode={handleToggleEditMode}
          handleToggle3dView={handleToggle3dView}
          openDownloadModal={openDownloadModal}
          handleDelete={handleDelete}
          handleUpdateQuantity={(quantity) =>
            nodeActions.updateNode(node, {
              quantity,
              presentation: data?.presentation,
            })
          }
          onClickOutside={() => setIsEditMode(false)}
          isPasteMode={isPasteMode}
          handleCopyNode={() => nodeActions.copyNode(node)}
          canDeleteInAddToProjectModal={canDeleteInAddToProjectModal}
        />
      </div>
    </div>
  );
};
